/* LEGAL-NOTICE
   © Copyright 2018 - Quid Informatica S.p.A. - All Rights Reserved
   https://quidinfo.it/qinetic/licence.html - "Quid Consumer Lending Platform"
   Deposito SIAE n. 012702 del 26 Luglio 2018
LEGAL-NOTICE */

import { paddedMonth, paddedDay } from './utils';

export abstract class ValueConverter<T> {
  protected value: any;

  constructor(value: any) {
    this.value = value;
  }

  protected toPlainString(): string {
    if (typeof this.value === 'number') {
      return String(this.value);
    } else if (typeof this.value === 'string') {
      return this.value;
    } else if (this.value === null || typeof this.value === 'undefined') {
      return null;
    } else {
      return '';
    }
  }

  /**
   * stabilisce se il value è un numero accettabile
   */
  protected isAcceptableNumber(value: any): boolean {
    return value !== null && !isNaN(value);
  }

  /**
   * stabilisce se il value è una stringa accettabile
   */
  protected isAcceptableString(value: any): boolean {
    return typeof value === 'string' && !!value;
  }

  /**
   * stabilisce se il value è inizilizzatore della classe Date accettabile
   */
  protected isAcceptableDate(value: any): boolean {
    return value instanceof Date && !isNaN(value.getTime());
  }

  /**
   * converte il value nel formato richiesto
   */
  abstract convertTo(format?: T): string;
}

/**
 * converte il value nel formata data richiesto
 */
export class DateConverter extends ValueConverter<'yyyymmdd' | 'millisec'> {
  protected value: any;

  constructor(value: any) {
    super(value);
  }

  /**
   * converte secondo il formato richiesto
   */
  convertTo(format?: 'yyyymmdd' | 'millisec' | 'iso'): string {
    switch (format) {
      case 'yyyymmdd':
        return this.toYYYYMMDD();
      case 'millisec':
        return this.toMillisec();
      case 'iso':
        return this.toISO();
      default:
        return this.toPlainString();
    }
  }

  /**
   * converte nel formato yyyy-mm-dd
   */
  toYYYYMMDD(): string {
    const dt = this.createDate();

    if (!this.isAcceptableDate(dt)) {
      return null;
    }

    const yyyy = dt.getFullYear();
    const mm = dt.getMonth() + 1;
    const dd = dt.getDate();

    return `${yyyy}-${paddedMonth(mm)}-${paddedDay(dd)}`;
  }

  /**
   * converte in formato millisecondi
   */
  toMillisec(): string {
    const dt = this.createDate();

    if (!this.isAcceptableDate(dt)) {
      return null;
    }

    const yyyy = dt.getFullYear();
    const mm = dt.getMonth();
    const dd = dt.getDate();

    return Date.UTC(yyyy, mm, dd).toString();
  }

  /**
   * converte in formato millisecondi
   */
  toISO(): string {
    const dt = this.createDate();

    if (!this.isAcceptableDate(dt)) {
      return null;
    }

    const yyyy = dt.getFullYear();
    const mm = dt.getMonth();
    const dd = dt.getDate();

    return new Date(Date.UTC(yyyy, mm, dd)).toISOString();
  }

  /**
   * crea oggetto data partendo dal value
   */
  private createDate(): Date {
    if (this.isAcceptableNumber(this.value)) {
      return new Date(Number(this.value));
    } else if (this.isAcceptableString(this.value)) {
      return new Date(this.value);
    } else {
      return null;
    }
  }
}

/**
 * converte il value in una string nel formato richiesto
 */
export class StringConverter extends ValueConverter<'uppercase'> {
  protected value: any;

  constructor(value: any) {
    super(value);
  }

  /**
   * converte secondo il formato richiesto
   */
  convertTo(format?: 'uppercase'): string {
    switch (format) {
      case 'uppercase':
        return this.toUppercase();
      default:
        return this.toPlainString();
    }
  }

  /**
   * converte la stringa in uppercase
   */
  protected toUppercase(): string {
    if (this.isAcceptableString(this.value)) {
      return (this.value as string).toUpperCase();
    } else {
      return null;
    }
  }
}
